import React, { useState } from 'react';
import {Form, Input, message, Select, Typography, Spin} from 'antd';
import {BankOutlined, IdcardOutlined, LockOutlined, MailOutlined, UserOutlined} from '@ant-design/icons';
import '../assets/styles/SignUp.css';
import {doCreateUserWithRollback} from '../firebase/auth';
import {useNavigate} from 'react-router-dom';
import {createUser} from "../services/UserServices";
import {useAuth} from '../contexts/authContext/index';

const { Title, Text } = Typography;
const { Option } = Select;

const SignUp = () => {
  const navigate = useNavigate();
  const { setRegisteringState } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const onFinish = async (values) => {
    const { username, email, password, confirm, cuit, empresa, rol } = values;

    if (password !== confirm) {
      return;
    }

    try {
      // Activar estados de loading
      setLoading(true);
      setRegisteringState(true);
      
      // Usar la función transaccional con rollback automático
      const result = await doCreateUserWithRollback(
        email, 
        password, 
        username, 
        cuit, 
        empresa, 
        rol,
        createUser // Callback para crear usuario en BD
      );
      
      console.log('Usuario registrado con éxito en ambos sistemas:', result);
      message.success('Usuario registrado con éxito');
      
      // Desactivar estados de loading
      setLoading(false);
      setRegisteringState(false);
      navigate('/');
    } catch (error) {
      console.error("Error al registrar el usuario:", error.message);
      
      // Mensajes de error más específicos
      if (error.message.includes('Usuario huérfano')) {
        message.error('Error crítico: Usuario creado en Firebase pero no en nuestra base de datos. Contacta soporte técnico.');
      } else if (error.message.includes('auth/email-already-in-use')) {
        message.error('Este correo electrónico ya está registrado');
      } else if (error.message.includes('auth/weak-password')) {
        message.error('La contraseña es demasiado débil');
      } else if (error.message.includes('Error al crear el usuario')) {
        message.error('Error al guardar los datos del usuario. Inténtalo de nuevo.');
      } else {
        message.error('Hubo un problema al registrar el usuario. Inténtalo de nuevo.');
      }
      
      // Siempre desactivar estados de loading en caso de error
      setLoading(false);
      setRegisteringState(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-form-container">
          <Title level={2} className="signup-title">Crea tu cuenta</Title>
          <Text type="secondary" className="signup-subtitle">Únete a nosotros y comienza tu viaje.</Text>

          <Form
            name="normal_signup"
            className="signup-form"
            initialValues={{ remember: true }}
            onFinish={onFinish}
          >
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: '',
                },
                {
                  pattern: /^[^\s]+ [^\s]+$/,
                  message: 'Ingresa nombre y apellido',
                },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Nombre completo"
                className="signup-input"
              />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: '' },
                { type: 'email', message: 'Ingresa un correo electrónico válido.' }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Correo electrónico"
                className="signup-input"
              />
            </Form.Item>

            <Form.Item
              name="cuit"
              rules={[
                { required: true, message: '' },
                { pattern: /^\d{11}$/, message: 'El CUIT debe tener 11 dígitos.' }
              ]}
            >
              <Input
                prefix={<IdcardOutlined />}
                placeholder="CUIT (11 dígitos)"
                className="signup-input"
              />
            </Form.Item>

            <Form.Item
              name="empresa"
              rules={[
                { required: true, message: '' },
                { min: 2, message: 'Debe tener al menos 2 caracteres' }
              ]}
            >
              <Input
                prefix={<BankOutlined />}
                placeholder="Empresa"
                className="signup-input"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: '' },
                { min: 6, message: 'La contraseña debe tener al menos 6 caracteres.' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Contraseña"
                className="signup-input"
              />
            </Form.Item>

            <Form.Item
              name="confirm"
              dependencies={['password']}
              rules={[
                { required: true, message: '' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Las contraseñas no coinciden'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirmar contraseña"
                className="signup-input"
              />
            </Form.Item>
            <Form.Item
              name="rol"
              rules={[
                { required: true, message: '' }
              ]}
            >
              <Select placeholder="Selecciona un rol" className="signup-input">
                <Option value="cliente">Cliente</Option>
                <Option value="vendedor">Vendedor</Option>
                <Option value="admin">Admin</Option>
              </Select>
            </Form.Item>

            <Form.Item className="signup-button-container">
              <button 
                type="primary" 
                htmlType="submit" 
                className="signup-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spin size="small" style={{ marginRight: '8px' }} />
                    Registrando...
                  </>
                ) : (
                  'Registrarse'
                )}
              </button>
            </Form.Item>
          </Form>

          <Text type="secondary" className="signup-footer">
            ¿Ya tienes una cuenta? <a href="/signin">Inicia sesión</a>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default SignUp;